import { Link } from "react-router-dom";
import {
  BUSINESS_NAME,
  LOGO_URL,
  PHONE,
  PHONE_HREF,
  EMAIL,
  HOURS,
  ADDRESS,
  FACEBOOK_URL,
  SERVICE_AREAS,
  NAV_LINKS,
  SERVICES,
} from "../../mocks/businessData";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-charcoal text-stone-400">
      {/* Main footer */}
      <div className="w-full px-4 md:px-8 lg:px-16 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Col 1: Brand */}
          <div className="lg:col-span-1">
            <div className="w-40 h-12 mb-5">
              <img
                src={LOGO_URL}
                alt={`${BUSINESS_NAME} logo`}
                className="w-full h-full object-contain brightness-200"
              />
            </div>
            <p className="font-sans text-sm leading-relaxed text-stone-400 mb-6 max-w-xs">
              {BUSINESS_NAME} specializes in custom landscape design, stonework,
              water features, and outdoor spaces built to last across London and
              Southwestern Ontario.
            </p>
            <div className="flex items-center gap-3">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-stone-700 text-stone-400 hover:border-rust-500 hover:text-white transition-all cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-sm"></i>
              </a>
            </div>
          </div>

          {/* Col 2: Services */}
          <div>
            <h5 className="font-sans text-white text-sm font-semibold tracking-widest uppercase mb-5">
              Services
            </h5>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <Link
                    to="/services"
                    className="font-sans text-sm text-stone-400 hover:text-white transition-colors cursor-pointer"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-stone-800">
              <h5 className="font-sans text-white text-sm font-semibold tracking-widest uppercase mb-4">
                Quick Links
              </h5>
              <ul className="space-y-2">
                {NAV_LINKS.map((l) => (
                  <li key={l.href}>
                    <Link
                      to={l.href}
                      className="font-sans text-sm text-stone-400 hover:text-white transition-colors cursor-pointer"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Col 3: Service Areas */}
          <div>
            <h5 className="font-sans text-white text-sm font-semibold tracking-widest uppercase mb-5">
              Areas We Serve
            </h5>
            <ul className="space-y-2">
              {SERVICE_AREAS.slice(0, 8).map((area) => (
                <li key={area} className="flex items-center gap-2">
                  <i className="ri-map-pin-2-fill text-rust-500 text-xs"></i>
                  <span className="font-sans text-sm text-stone-400">{area}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Contact */}
          <div>
            <h5 className="font-sans text-white text-sm font-semibold tracking-widest uppercase mb-5">
              Contact
            </h5>
            <ul className="space-y-4">
              <li>
                <a
                  href={PHONE_HREF}
                  className="flex items-start gap-3 group cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rust-700/20 mt-0.5 flex-shrink-0">
                    <i className="ri-phone-fill text-rust-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="font-sans text-xs text-stone-500 mb-0.5">Phone</p>
                    <span className="font-sans text-sm text-white group-hover:text-rust-300 transition-colors font-medium">
                      {PHONE}
                    </span>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-start gap-3 group cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rust-700/20 mt-0.5 flex-shrink-0">
                    <i className="ri-mail-fill text-rust-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="font-sans text-xs text-stone-500 mb-0.5">Email</p>
                    <span className="font-sans text-sm text-white group-hover:text-rust-300 transition-colors font-medium break-all">
                      {EMAIL}
                    </span>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rust-700/20 mt-0.5 flex-shrink-0">
                  <i className="ri-map-pin-fill text-rust-400 text-sm"></i>
                </div>
                <div>
                  <p className="font-sans text-xs text-stone-500 mb-0.5">Address</p>
                  <a
                    href="https://maps.google.com/maps?q=563+Wonderland+Rd+N,+London,+ON+N6H+3E2"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="font-sans text-sm text-white hover:text-rust-300 transition-colors font-medium cursor-pointer"
                  >
                    {ADDRESS}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rust-700/20 mt-0.5 flex-shrink-0">
                  <i className="ri-time-fill text-rust-400 text-sm"></i>
                </div>
                <div>
                  <p className="font-sans text-xs text-stone-500 mb-0.5">Hours</p>
                  <span className="font-sans text-sm text-white font-medium">{HOURS}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-stone-800 px-4 md:px-8 lg:px-16 py-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-sans text-xs text-stone-600 text-center sm:text-left">
            &copy; {currentYear} {BUSINESS_NAME} &mdash; Upper Canada Landscaping Inc. | Serving London, Ontario &amp; Surrounding Area
          </p>
          <div className="flex items-center gap-4">
            <span className="font-sans text-xs text-stone-600">
              Licensed &amp; Insured
            </span>
            <span className="text-stone-700">|</span>
            <span className="font-sans text-xs text-stone-600">
              London, Ontario
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
